var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

import Orbit, { evented } from '@orbit/core';
import { deepGet } from '@orbit/utils';
import { QueryBuilder, buildQuery, TransformBuilder } from '@orbit/data';
import { AsyncOperationProcessor } from './async-operation-processor';
import AsyncCacheIntegrityProcessor from './operation-processors/async-cache-integrity-processor';
import AsyncSchemaConsistencyProcessor from './operation-processors/async-schema-consistency-processor';
import AsyncSchemaValidationProcessor from './operation-processors/async-schema-validation-processor';
import { AsyncPatchOperators } from './operators/async-patch-operators';
import { AsyncQueryOperators } from './operators/async-query-operators';
import { AsyncInversePatchOperators } from './operators/async-inverse-patch-operators';
const {
  assert
} = Orbit;
let AsyncRecordCache = class AsyncRecordCache {
  constructor(settings) {
    assert("AsyncRecordCache's `schema` must be specified in `settings.schema` constructor argument", !!settings.schema);
    this._schema = settings.schema;
    this._keyMap = settings.keyMap;
    this._queryBuilder = settings.queryBuilder || new QueryBuilder();
    this._transformBuilder = settings.transformBuilder || new TransformBuilder({
      recordInitializer: this._schema
    });
    this._queryOperators = settings.queryOperators || AsyncQueryOperators;
    this._patchOperators = settings.patchOperators || AsyncPatchOperators;
    this._inversePatchOperators = settings.inversePatchOperators || AsyncInversePatchOperators;
    const processors = settings.processors ? settings.processors : [AsyncSchemaValidationProcessor, AsyncSchemaConsistencyProcessor, AsyncCacheIntegrityProcessor];
    this._processors = processors.map(Processor => {
      let processor = new Processor(this);
      assert('Each processor must extend AsyncOperationProcessor', processor instanceof AsyncOperationProcessor);
      return processor;
    });
  }

  get schema() {
    return this._schema;
  }

  get keyMap() {
    return this._keyMap;
  }

  get queryBuilder() {
    return this._queryBuilder;
  }

  get transformBuilder() {
    return this._transformBuilder;
  }

  get processors() {
    return this._processors;
  }

  getQueryOperator(op) {
    return this._queryOperators[op];
  }

  getPatchOperator(op) {
    return this._patchOperators[op];
  }

  getInversePatchOperator(op) {
    return this._inversePatchOperators[op];
  }

  async getRelatedRecordAsync(identity, relationship) {
    const record = await this.getRecordAsync(identity);

    if (record) {
      return deepGet(record, ['relationships', relationship, 'data']);
    }

    return undefined;
  }

  async getRelatedRecordsAsync(identity, relationship) {
    const record = await this.getRecordAsync(identity);

    if (record) {
      return deepGet(record, ['relationships', relationship, 'data']);
    }

    return undefined;
  }
  /**
   * Queries the cache.
   */


  async query(queryOrExpression, options, id) {
    const query = buildQuery(queryOrExpression, options, id, this._queryBuilder);
    return await this._query(query.expression);
  }
  /**
   * Patches the cache with an operation or operations.
   */


  async patch(operationOrOperations) {
    if (typeof operationOrOperations === 'function') {
      operationOrOperations = operationOrOperations(this._transformBuilder);
    }

    const result = {
      inverse: [],
      data: []
    };

    if (Array.isArray(operationOrOperations)) {
      await this._applyPatchOperations(operationOrOperations, result, true);
    } else {
      await this._applyPatchOperation(operationOrOperations, result, true);
    }

    result.inverse.reverse();
    return result;
  } /////////////////////////////////////////////////////////////////////////////
  // Protected methods
  /////////////////////////////////////////////////////////////////////////////


  async _query(expression) {
    const queryOperator = this.getQueryOperator(expression.op);

    if (!queryOperator) {
      throw new Error(`Unable to find query operator: ${expression.op}`);
    }

    return await queryOperator(this, expression);
  }

  async _applyPatchOperations(ops, result, primary = false) {
    for (let op of ops) {
      await this._applyPatchOperation(op, result, primary);
    }
  }

  async _applyPatchOperation(operation, result, primary = false) {
    if (operation.op === 'replaceRecord') {
      Orbit.deprecate('The `replaceRecord` operation has been deprecated - use `updateRecord` instead.');
      operation = {
        op: 'updateRecord',
        record: operation.record
      };
    }

    for (let processor of this._processors) {
      await processor.validate(operation);
    }

    const inversePatchOperator = this.getInversePatchOperator(operation.op);
    const inverseOp = await inversePatchOperator(this, operation);

    if (inverseOp) {
      result.inverse.push(inverseOp); // Query and perform related `before` operations

      for (let processor of this._processors) {
        await this._applyPatchOperations((await processor.before(operation)), result);
      } // Query related `after` operations before performing
      // the requested operation. These will be applied on success.


      let preparedOps = [];

      for (let processor of this._processors) {
        preparedOps.push((await processor.after(operation)));
      } // Perform the requested operation


      let patchOperator = this.getPatchOperator(operation.op);
      let data = await patchOperator(this, operation);

      if (primary) {
        result.data.push(data);
      } // Query and perform related `immediate` operations


      for (let processor of this._processors) {
        await processor.immediate(operation);
      } // Emit event


      this.emit('patch', operation, data); // Perform prepared operations after performing the requested operation

      for (let ops of preparedOps) {
        await this._applyPatchOperations(ops, result);
      } // Query and perform related `finally` operations


      for (let processor of this._processors) {
        await this._applyPatchOperations((await processor.finally(operation)), result);
      }
    } else if (primary) {
      result.data.push(null);
    }
  }

};
AsyncRecordCache = __decorate([evented], AsyncRecordCache);
export { AsyncRecordCache };